import { useState } from 'react';
import { Box, HStack, VStack, Divider } from '@chakra-ui/react';
import { FaSyncAlt } from 'react-icons/fa';
import ButtonProc from 'components/common/Buttons/ButtonProc';
import { useUser } from 'contexts';
import { Icon, Text } from 'components/UI';
import { Specifications, specifications } from 'constants/specifications';
import { ActiveParameters, IBom, IQuoteJson } from 'types';
import { gradientStyle, tertiaryThemeGradientDark } from 'constants/theme';
import { LinkButton } from 'components/common/Buttons/LinkButton';
import { ConfiguratorTab } from 'constants/configurator';
import QuoteModal from 'components/Configurator/QuoteModal';
import OrderConfigModal, { OrderFormValues } from 'components/Configurator/OrderConfigModal';
import ErpModal from './ErpModal';
import BomModal from './BomModal';
import { IParameter, IProductResource } from '../../../../../server/types';
import OptionsMenuQuoteButton from '../OptionsMenuPreload/OptionsMenuQuoteButton';
import ProcessMenuItem from './ProcessMenuItem';
import ParameterGroup from '../ParameterGroup';

export type ProcessMenuProps = {
  product?: IProductResource;
  params: ActiveParameters;
  onTabChange?: (tab: ConfiguratorTab) => void;
  modelDownloadUrl: string;
  drawingDownloadUrl?: string;
  downloadExport2dDwg?: string;
  downloadExportDwg?: string;
  downloadExportSat?: string;
  downloadExportStp?: string;
  rfq?: boolean;
  disabled: boolean;
  skipDownloading: boolean;
  modelParameters?: IParameter[];
  resultFileJobId: string;
  selectedProduct: string;
  resultFileNameRef: string;
  quote?: IQuoteJson;
  quotePdfUrl: string;
  flc?: string;
  flcUrl?: string;
  bom?: IBom[];
  bomCsvUrl?: string;
};

const buttonStyles = {
  ...gradientStyle,
  _disabled: {
    bg: tertiaryThemeGradientDark,
    opacity: '0.4',
    cursor: 'not-allowed',
  },
  _focus: {
    boxShadow: 'none',
  },
  color: 'primaryFontColour.400',
  borderStyle: 'solid',
  textTransform: 'uppercase',
  w: '85%',
  p: 2,
};

enum ModalType {
  ERP = 'ERP',
  BOM = 'BOM',
  QUOTE = 'QUOTE',
  ORDER_CONFIG_FORM = 'ORDER_CONFIG_FORM ',
}

const ProcessMenu = ({
  params,
  product,
  disabled,
  downloadExportStp,
  downloadExportSat,
  downloadExportDwg,
  downloadExport2dDwg,
  modelDownloadUrl,
  drawingDownloadUrl,
  rfq,
  onTabChange,
  skipDownloading,
  resultFileJobId,
  selectedProduct,
  resultFileNameRef,
  quote,
  quotePdfUrl,
  modelParameters,
  flc,
  flcUrl,
  bom,
  bomCsvUrl,
}: ProcessMenuProps) => {
  const [user] = useUser();
  const [openModal, setOpenModal] = useState<ModalType>();

  if (!product) {
    return <Box px={4}>PLEASE SELECT A PRODUCT FROM THE PRODUCT MENU.</Box>;
  }

  const specCallbackMap = {
    [Specifications.DownloadSTEP]: downloadExportStp,
    [Specifications.DownloadSAT]: downloadExportSat,
    [Specifications.Download2DDWG]: downloadExport2dDwg,
    [Specifications.Download3DDWG]: downloadExportDwg,
    [Specifications.DownloadPDF]: drawingDownloadUrl,
    [Specifications.ViewBOM]: () => setOpenModal(ModalType.BOM),
    [Specifications.SendBOMPLM]: () => setOpenModal(ModalType.ERP),
    [Specifications.DownloadConfiguration]: modelDownloadUrl,
  };

  const checkUpToDate = () => {
    if (modelParameters && Object.keys(modelParameters).length !== 0) {
      return !Object.keys(params).some((key) => params[+key] !== modelParameters[+key]);
    }
    return false;
  };

  const isUpdated = checkUpToDate();

  const renderFields = () => {
    if (product === null) {
      return null;
    }

    const paramKeys = product.parameterList;

    const renderParameters = paramKeys?.map(({ title, pList }) => (
      <ParameterGroup
        paramKeys={paramKeys}
        title={title}
        key={title}
        buttonProps={{
          bgColor: 'base1Colour.500',
          color: 'primaryFontColour.500',
          fontWeight: 'bold',
          fontSize: 'sm',
          _hover: {
            bgColor: 'transparent',
          },
        }}
        accordionProps={{
          borderColor: 'transparent',
        }}
      >
        <ProcessMenuItem parameters={pList} params={params} isUpdated={isUpdated} />
      </ParameterGroup>
    ));

    return <Box w="full">{renderParameters}</Box>;
  };

  const renderOutofDateMessage = !isUpdated ? (
    <Box>
      <HStack
        sx={{
          alignItems: 'center',
          color: 'base1FontColour.400',
          fontSize: 'sm',
          borderRadius: 6,
          p: 4,
          w: 'full',
        }}
      >
        <Box>
          <Icon as={FaSyncAlt} size="lg" />
        </Box>
        <Text color="base1FontColour.400">The model and downloads aren&apos;t up to date with the selections.</Text>
      </HStack>
    </Box>
  ) : null;

  const isDisabled = disabled || !isUpdated;

  if (disabled) {
    return <Box px={4}>{renderOutofDateMessage}</Box>;
  }

  const availableSpecifications = specifications.filter((specification) =>
    (user?.role?.specifications ?? []).includes(specification.id)
  );

  const renderSpecifications = availableSpecifications.map(({ id, text }) => {
    const textArr = text.split(' ');
    const first = textArr[0];
    const second = text.replace(textArr[0], '');

    const specValue = specCallbackMap[id as keyof typeof specCallbackMap];

    return typeof specValue === 'function' ? (
      <ButtonProc
        key={id}
        first={first}
        second={second}
        onClick={specValue}
        isDisabled={disabled}
        sx={buttonStyles}
        size="sm"
      />
    ) : (
      <LinkButton href={specValue} sx={buttonStyles} key={id}>
        <Text color="primaryFontColour.400" letterSpacing={2}>
          {first}
          <Text as="span" fontWeight="bold" color="primaryFontColour.400" letterSpacing={2}>
            {second}
          </Text>
        </Text>
      </LinkButton>
    );
  });

  const renderSpecificationDivider =
    availableSpecifications.length > 0 ? (
      <Divider
        sx={{
          w: 'full',
          maxW: 250,
          borderBottomWidth: 2,
          opacity: 1,
        }}
      />
    ) : null;
  return (
    <VStack
      sx={{
        flexGrow: 1,
        justifyContent: 'space-between',
        overflowY: 'auto',
        pb: 5,
        mt: 2,
        w: 'full',
        '&::-webkit-scrollbar': {
          w: 2.5,
        },
        '&::-webkit-scrollbar-thumb': {
          bgColor: 'scrollbarThumbColour.400',
        },
      }}
    >
      {renderFields()}
      <VStack w="full">
        <OptionsMenuQuoteButton
          first="VIEW"
          second="QUOTE"
          isDisabled={isDisabled && !skipDownloading}
          onClick={() => setOpenModal(ModalType.QUOTE)}
          w="85%"
          hidden={rfq}
        />
        <OptionsMenuQuoteButton
          first={rfq ? 'REQUEST' : 'ORDER'}
          second={rfq ? 'QUOTATION' : 'CONFIGURATION'}
          onClick={() => setOpenModal(ModalType.ORDER_CONFIG_FORM)}
          isDisabled={isDisabled && !skipDownloading}
          w="85%"
        />

        {renderSpecificationDivider}
        {renderSpecifications}

        <Divider
          sx={{
            w: 'full',
            maxW: 250,
            borderBottomWidth: 2,
            opacity: 1,
          }}
        />
        <ButtonProc
          first="CHANGE"
          second="CONFIGURATION"
          onClick={() => onTabChange?.(ConfiguratorTab.CONFIGURE)}
          isDisabled={false}
          sx={buttonStyles}
          size="sm"
          w="85%"
          p={2}
          mt={1}
        />
      </VStack>
      <OrderConfigModal
        data={(user as OrderFormValues) ?? null}
        viewParameters={params}
        rfq={rfq}
        product={product}
        isOpen={openModal === ModalType.ORDER_CONFIG_FORM}
        onClose={() => setOpenModal(undefined)}
        resultFileJobId={resultFileJobId}
        selectedProduct={selectedProduct}
        resultFileNameRef={resultFileNameRef}
      />
      <QuoteModal
        isOpen={openModal === ModalType.QUOTE}
        quote={quote}
        closePopup={() => setOpenModal(undefined)}
        quotePdfUrl={quotePdfUrl as string}
      />
      <ErpModal
        isOpen={openModal === ModalType.ERP}
        closePopup={() => setOpenModal(undefined)}
        flc={flc}
        flcUrl={flcUrl}
        bom={bom as IBom[]}
      />
      <BomModal
        isOpen={openModal === ModalType.BOM}
        bomRows={bom}
        closePopup={() => setOpenModal(undefined)}
        bomCsvUrl={bomCsvUrl}
      />
    </VStack>
  );
};

export default ProcessMenu;
